import React from 'react';
import { SocialLink, VirtualTourConfig } from '../types/profile';
import { useLocalization } from '../engine/localization/context';
import { Globe, Phone, PhoneCall, FileText, Mail, MapPin } from 'lucide-react';

interface SocialBarProps {
  socials: SocialLink[];
  virtualTour?: VirtualTourConfig;
}

export const SocialBar: React.FC<SocialBarProps> = ({ socials, virtualTour }) => {
  const { resolve, t } = useLocalization();

  if ((!socials || socials.length === 0) && !virtualTour?.ecosystemUrl && !virtualTour?.url) {
    return null;
  }

  // Authentic Brand Colors & Optical Semantic Icons
  const renderIcon = (platform: string, customIcon?: string) => {
    if (customIcon) {
      return <i className={`${customIcon} text-lg`} />;
    }

    switch (platform.toLowerCase()) {
      case 'office-phone':
      case 'tel':
      case 'landline':
      case 'phone-office':
        return <PhoneCall className="w-5 h-5 text-sky-400 select-none pointer-events-none" />;

      case 'phone':
      case 'mobile':
        return <Phone className="w-5 h-5 text-emerald-400 select-none pointer-events-none" />;

      case 'website':
      case 'web':
        return <Globe className="w-5 h-5 text-sky-400 select-none pointer-events-none" />;

      case 'profile':
      case 'old-profile':
      case 'document':
      case 'doc':
        return <FileText className="w-5 h-5 text-amber-400 select-none pointer-events-none" />;

      case 'email':
      case 'mail':
        return <Mail className="w-5 h-5 text-purple-400 select-none pointer-events-none" />;

      case 'maps':
      case 'location':
      case 'map':
        return <MapPin className="w-5 h-5 text-rose-400 select-none pointer-events-none" />;

      case 'whatsapp':
        // WhatsApp brand green #25D366 with balanced optical scale (w-[22px] h-[22px])
        return (
          <svg
            className="w-[22px] h-[22px] text-[#25D366] fill-current select-none pointer-events-none transition-transform"
            viewBox="0 0 448 512"
            aria-hidden="true"
          >
            <path d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z" />
          </svg>
        );

      case 'facebook':
        // Facebook brand blue #1877F2
        return (
          <svg
            className="w-4 h-5 text-[#1877F2] fill-current select-none pointer-events-none"
            viewBox="0 0 320 512"
            aria-hidden="true"
          >
            <path d="M279.14 288l14.22-92.66h-88.91v-60.13c0-25.35 12.42-50.06 52.24-50.06h40.42V6.26S260.43 0 225.36 0c-73.22 0-121.08 44.38-121.08 124.72v70.62H22.89V288h81.39v224h100.17V288z" />
          </svg>
        );

      case 'instagram':
        // Instagram vibrant brand color #E4405F / #E1306C
        return (
          <svg
            className="w-5 h-5 text-[#E4405F] fill-current select-none pointer-events-none"
            viewBox="0 0 448 512"
            aria-hidden="true"
          >
            <path d="M224.1 141c-63.6 0-114.9 51.3-114.9 114.9s51.3 114.9 114.9 114.9S339 319.5 339 255.9 287.7 141 224.1 141zm0 189.6c-41.1 0-74.7-33.5-74.7-74.7s33.5-74.7 74.7-74.7 74.7 33.5 74.7 74.7-33.6 74.7-74.7 74.7zm146.4-194.3c0 14.9-12 26.8-26.8 26.8-14.9 0-26.8-12-26.8-26.8s12-26.8 26.8-26.8 26.8 12 26.8 26.8zm76.1 27.2c-1.7-35.9-9.9-67.7-36.2-93.9-26.2-26.2-58-34.4-93.9-36.2-37-2.1-147.9-2.1-184.9 0-35.8 1.7-67.6 9.9-93.9 36.1s-34.4 58-36.2 93.9c-2.1 37-2.1 147.9 0 184.9 1.7 35.9 9.9 67.7 36.2 93.9s58 34.4 93.9 36.2c37 2.1 147.9 2.1 184.9 0 35.9-1.7 67.7-9.9 93.9-36.2 26.2-26.2 34.4-58 36.2-93.9 2.1-37 2.1-147.8 0-184.8zM398.8 388c-7.8 19.6-22.9 34.7-42.6 42.6-29.5 11.7-99.5 9-132.1 9s-102.7 2.6-132.1-9c-19.6-7.8-34.7-22.9-42.6-42.6-11.7-29.5-9-99.5-9-132.1s-2.6-102.7 9-132.1c7.8-19.6 22.9-34.7 42.6-42.6 29.5-11.7 99.5-9 132.1-9s102.7-2.6 132.1 9c19.6 7.8 34.7 22.9 42.6 42.6 11.7 29.5 9 99.5 9 132.1s2.7 102.7-9 132.1z" />
          </svg>
        );

      case 'youtube':
        // YouTube brand red #FF0000
        return (
          <svg
            className="w-5 h-5 text-[#FF0000] fill-current select-none pointer-events-none"
            viewBox="0 0 576 512"
            aria-hidden="true"
          >
            <path d="M549.655 124.083c-6.281-23.65-24.787-42.276-48.284-48.597C458.781 64 288 64 288 64S117.22 64 74.629 75.486c-23.497 6.322-42.003 24.947-48.284 48.597-11.412 42.867-11.412 132.305-11.412 132.305s0 89.438 11.412 132.305c6.281 23.65 24.787 41.5 48.284 47.821C117.22 448 288 448 288 448s170.78 0 213.371-11.486c23.497-6.321 42.003-24.171 48.284-47.821 11.412-42.867 11.412-132.305 11.412-132.305s0-89.438-11.412-132.305zm-317.51 213.583V175.185l142.739 81.205-142.739 81.276z" />
          </svg>
        );

      case 'linkedin':
        // LinkedIn brand blue #0A66C2
        return (
          <svg
            className="w-5 h-5 text-[#0A66C2] fill-current select-none pointer-events-none"
            viewBox="0 0 448 512"
            aria-hidden="true"
          >
            <path d="M100.28 448H7.4V148.9h92.88zM53.79 108.1C24.09 108.1 0 83.5 0 53.8a53.79 53.79 0 0 1 107.58 0c0 29.7-24.1 54.3-53.79 54.3zM447.9 448h-92.68V302.4c0-34.7-.7-79.2-48.29-79.2-48.29 0-55.69 37.7-55.69 76.7V448h-92.78V148.9h89.08v40.8h1.3c12.4-23.5 42.69-48.3 87.88-48.3 94 0 111.28 61.9 111.28 142.3V448z" />
          </svg>
        );

      case 'twitter':
      case 'x':
        // X / Twitter authentic white
        return (
          <svg
            className="w-4 h-4 text-white fill-current select-none pointer-events-none"
            viewBox="0 0 512 512"
            aria-hidden="true"
          >
            <path d="M389.2 48h70.6L305.6 224.2 487 464H345L233.7 318.6 106.5 464H35.8L200.7 275.5 26.8 48H172.4L272.9 180.9 389.2 48zM364.4 421.8h39.1L151.1 88h-42L364.4 421.8z" />
          </svg>
        );

      case 'github':
        return (
          <svg
            className="w-5 h-5 text-slate-100 fill-current select-none pointer-events-none"
            viewBox="0 0 496 512"
            aria-hidden="true"
          >
            <path d="M165.9 397.4c0 2-2.3 3.6-5.2 3.6-3.3.3-5.6-1.3-5.6-3.6 0-2 2.3-3.6 5.2-3.6 3-.3 5.6 1.3 5.6 3.6zm-31.1-4.5c-.7 2 1.3 4.3 4.3 4.9 2.6 1 5.6 0 6.2-2s-1.3-4.3-4.3-5.2c-2.6-.7-5.5.3-6.2 2.3zm44.2-1.7c-2.9.7-4.9 2.6-4.6 4.9.3 2 2.9 3.3 5.9 2.6 2.9-.7 4.9-2.6 4.6-4.6-.3-1.9-3-3.2-5.9-2.9zM244.8 8C106.1 8 0 113.3 0 252c0 110.9 69.8 205.8 169.5 239.2 12.8 2.3 17.3-5.6 17.3-12.1 0-6.2-.3-40.4-.3-61.4 0 0-70 15-84.7-29.8 0 0-11.4-29.1-27.8-36.6 0 0-22.9-15.7 1.6-15.4 0 0 24.9 2 38.6 25.8 21.9 38.6 58.6 27.5 72.9 20.9 2.3-16 8.8-27.1 16-33.7-55.9-6.2-112.3-14.3-112.3-110.5 0-27.5 7.6-41.3 23.6-58.9-2.6-6.5-11.1-33.3 2.6-67.9 20.9-6.5 69 27 69 27 20-5.6 41.5-8.5 62.8-8.5s42.8 2.9 62.8 8.5c0 0 48.1-33.6 69-27 13.7 34.7 5.2 61.4 2.6 67.9 16 17.7 25.8 31.5 25.8 58.9 0 96.5-58.9 104.2-114.8 110.5 9.2 7.9 17 22.9 17 46.4 0 33.7-.3 75.4-.3 83.6 0 6.5 4.6 14.4 17.3 12.1C428.2 457.8 496 362.9 496 252 496 113.3 383.5 8 244.8 8z" />
          </svg>
        );

      case 'tiktok':
        return (
          <svg
            className="w-4 h-5 text-[#25F4EE] fill-current select-none pointer-events-none"
            viewBox="0 0 448 512"
            aria-hidden="true"
          >
            <path d="M448,209.91a210.06,210.06,0,0,1-122.77-39.25V349.38A162.55,162.55,0,1,1,185,188.31V278.2a74.62,74.62,0,1,0,52.23,71.18V0l88,0a121.18,121.18,0,0,0,1.86,22.17h0A122.18,122.18,0,0,0,381,102.39a121.43,121.43,0,0,0,67,20.14Z" />
          </svg>
        );

      case 'virtualtour':
      case 'vr':
        // VR Cardboard / Metaverse headset icon (Font Awesome 6 fa-vr-cardboard)
        return (
          <svg
            className="w-5 h-5 text-[#38bdf8] fill-current select-none pointer-events-none"
            viewBox="0 0 640 512"
            aria-hidden="true"
          >
            <path d="M576 64H64C28.65 64 0 92.65 0 128v256c0 35.35 28.65 64 64 64h512c35.35 0 64-28.65 64-64V128c0-35.35-28.65-64-64-64zM176 336c-44.18 0-80-35.82-80-80s35.82-80 80-80 80 35.82 80 80-35.82 80-80 80zm144-80c0-14.7 6.4-27.9 16.5-37.1 12.3-11.2 28.8-18.9 47.5-18.9 18.7 0 35.2 7.7 47.5 18.9 10.1 9.2 16.5 22.4 16.5 37.1s-6.4 27.9-16.5 37.1c-12.3 11.2-28.8 18.9-47.5 18.9-18.7 0-35.2-7.7-47.5-18.9-10.1-9.2-16.5-22.4-16.5-37.1zm144 80c-44.18 0-80-35.82-80-80s35.82-80 80-80 80 35.82 80 80-35.82 80-80 80z" />
          </svg>
        );

      default:
        return <Globe className="w-5 h-5 text-slate-300 select-none pointer-events-none" />;
    }
  };

  // Check if Virtual Tour ecosystem link is configured
  const vrUrl = virtualTour?.ecosystemUrl || (virtualTour?.enabled ? virtualTour?.url : undefined);
  const vrLabel = virtualTour?.label ? resolve(virtualTour.label) : t('social.virtualTour');

  return (
    <nav
      aria-label="Social & External Profiles"
      className="flex items-center justify-center flex-wrap gap-2.5 sm:gap-3 my-4"
    >
      {socials.map((item) => {
        const label = item.label ? resolve(item.label) : item.platform;
        return (
          <a
            key={item.id}
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={label}
            title={label}
            className="w-11 h-11 rounded-full bg-slate-800/80 hover:bg-slate-700/90 border border-slate-700/60 hover:border-slate-500/80 flex items-center justify-center transition-all duration-200 hover:scale-110 shadow-sm focus:outline-none focus:ring-2 focus:ring-sky-400/50"
          >
            {renderIcon(item.platform, item.icon)}
          </a>
        );
      })}

      {/* Reusable Circular VR / Metaverse Link in the Same Row */}
      {vrUrl && (
        <a
          href={vrUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={vrLabel}
          title={vrLabel}
          className="w-11 h-11 rounded-full bg-slate-800/80 hover:bg-slate-700/90 border border-sky-500/40 hover:border-sky-400/80 hover:shadow-[0_0_15px_rgba(56,189,248,0.3)] flex items-center justify-center transition-all duration-200 hover:scale-110 shadow-sm focus:outline-none focus:ring-2 focus:ring-sky-400/50"
        >
          {renderIcon('vr', virtualTour?.icon)}
        </a>
      )}
    </nav>
  );
};


